/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { OrderService } from '@services';
import {
  ShippingPaginationResult,
  ShippingItemTime,
  Quote,
  LeadOrder,
} from '@interfaces';
import { CommercialAgreementDialogComponent } from 'src/app/shared/components/dialogs/commercial-agreement-dialog/commercial-agreement-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class LeadOrderHelpersService {
  private options: Intl.DateTimeFormatOptions = { weekday: 'long' };
  private formatDay: Intl.DateTimeFormat = new Intl.DateTimeFormat(
    'es-ES',
    this.options,
  );

  constructor(
    private orderService: OrderService,
    private dialog: MatDialog,
  ) {}

  /**
   * Formats calendar grid data by adding day names to availability items
   */
  formatCalendarGridData(
    data: ShippingPaginationResult,
  ): ShippingItemTime[] | undefined {
    return (data?.results || []).map(obj => {
      const newArray = obj?.availability.map(item => {
        const dateObj = new Date(item.startDateTime);
        const dayName = this.formatDay.format(dateObj);
        return { ...item, dayName };
      });
      return { ...obj, availability: newArray };
    });
  }

  /**
   * Opens a link in a new tab
   */
  openLinkInNewTab(link: string): void {
    const a = document.createElement('a');
    a.href = link;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  /**
   * Gets quote detail and updates loading state
   */
  getQuoteDetail(
    orderId: string,
    onSuccess: (quote: Quote) => void,
  ): Observable<Quote> {
    return this.orderService.getQuoteDetail(orderId).pipe(
      tap((quote: Quote) => {
        onSuccess(quote);
      }),
    );
  }

  /**
   * Opens commercial agreement dialog
   */
  openCommercialAgreementDialog(
    lead: LeadOrder,
    onSetCalendarGrid: any,
    onSuccess: () => void,
  ): MatDialogRef<CommercialAgreementDialogComponent> | null {
    if (lead && lead?.items && lead?.items.length > 0) {
      const dialogRef = this.dialog.open(CommercialAgreementDialogComponent, {
        data: {
          lead,
          onSetCalendarGrid,
        },
        disableClose: false,
        autoFocus: false,
        width: 'auto',
        height: 'auto',
        panelClass: 'quick-answers-dialog',
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'success') {
          onSuccess();
        }
      });

      return dialogRef;
    }
    return null;
  }

  /**
   * Updates order note
   */
  updateOrderNote(
    orderId: string,
    notes: string,
    onComplete: () => void,
  ): void {
    const leadNote = { notes };

    if (orderId) {
      this.orderService.updateNote(orderId, leadNote).subscribe({
        next: () => {
          onComplete();
        },
        error: () => {
          onComplete();
        },
      });
    }
  }

  /**
   * Handles quote action with loading state management
   */
  handleQuoteAction(
    orderId: string,
    onStartLoading: () => void,
    onSuccess: (quote: Quote, orderId: string) => void,
    onCloseQuote?: () => void,
    type?: string,
  ): void {
    onStartLoading();
    this.getQuoteDetail(orderId, (quote: Quote) => {
      if (type && onCloseQuote) {
        onCloseQuote();
      }
      onSuccess(quote, orderId);
    }).subscribe();
  }

  /**
   * Handles update note with loading state management
   */
  handleUpdateNote(
    orderId: string | undefined,
    formValue: string,
    onStartLoading: () => void,
    onComplete: () => void,
  ): void {
    onStartLoading();
    if (orderId) {
      this.updateOrderNote(orderId, formValue, onComplete);
    }
  }

  /**
   * Handles opening agreement dialog with state management
   */
  handleOpenAgreement(
    lead: LeadOrder,
    onSetCalendarGrid: any,
    onSuccessRefresh: () => void,
    onDialogOpened: (
      dialogRef: MatDialogRef<CommercialAgreementDialogComponent>,
    ) => void,
    onShowEmptyMessage: () => void,
  ): void {
    const dialogRef = this.openCommercialAgreementDialog(
      lead,
      onSetCalendarGrid,
      onSuccessRefresh,
    );

    if (dialogRef) {
      onDialogOpened(dialogRef);
    } else {
      onShowEmptyMessage();
    }
  }
}
